import * as React from 'react';

export interface EmptyBoxIllustrationProps extends React.SVGProps<SVGSVGElement> {
  className?: string;
}

/**
 * The open-box empty-state illustration, extracted verbatim from the
 * Noor.Merchant Figma file (node 24:7145 — the "Нет продукта" / "Нет акций"
 * zero-state). Unlike the monochrome `Menu*Icon` glyphs this is a full-colour
 * illustration with fixed brand tints, so it does not follow `currentColor`;
 * callers size it with `size-*` (its native frame is ~164×134, drawn on a
 * 160×160 square with top padding to match the design's "Switch container").
 */
export function EmptyBoxIllustration({ className, ...props }: EmptyBoxIllustrationProps) {
  return (
    <svg
      viewBox="0 0 163.714 148"
      fill="none"
      aria-hidden="true"
      className={className}
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <g transform="translate(0 14)">
        <path
          d="M147.993 67.1912C147.993 82.1003 143 96.1097 134.68 107.163C130.072 113.204 124.568 118.473 118.168 122.586C107.928 129.398 95.5119 133.254 82.3278 133.254C46.1034 133.382 16.6631 103.821 16.6631 67.1912C16.6631 30.6897 45.9754 1 82.3278 1C95.5119 1 107.8 4.8558 118.168 11.6677C124.568 15.7806 130.072 21.0502 134.68 27.0909C143 38.2727 147.993 52.1536 147.993 67.1912Z"
          fill="#F1F3F9"
          stroke="#D6DCE8"
          strokeWidth="2"
          strokeMiterlimit="10"
        />
        <path
          d="M32.3198 58.341H31.5271C30.9326 58.341 30.5362 58.1561 30.5362 57.8786C30.5362 57.6012 30.9326 57.4162 31.5271 57.4162H32.3198C32.9144 57.4162 33.3108 57.6012 33.3108 57.8786C33.3108 58.1561 32.9144 58.341 32.3198 58.341Z"
          fill="#2D5887"
        />
        <path
          d="M32.3198 62.0405H31.5271C30.9326 62.0405 30.5362 61.8555 30.5362 61.578C30.5362 61.3006 30.9326 61.1156 31.5271 61.1156H32.3198C32.9144 61.1156 33.3108 61.3006 33.3108 61.578C33.3108 61.8555 32.9144 62.0405 32.3198 62.0405Z"
          fill="#2D5887"
        />
        <path
          d="M32.3198 65.7399H31.5271C30.9326 65.7399 30.5362 65.5549 30.5362 65.2775C30.5362 65 30.9326 64.815 31.5271 64.815H32.3198C32.9144 64.815 33.3108 65 33.3108 65.2775C33.3108 65.5549 32.9144 65.7399 32.3198 65.7399Z"
          fill="#2D5887"
        />
        <path
          d="M32.3198 69.4393H31.5271C30.9326 69.4393 30.5362 69.2544 30.5362 68.9769C30.5362 68.6994 30.9326 68.5145 31.5271 68.5145H32.3198C32.9144 68.5145 33.3108 68.6994 33.3108 68.9769C33.3108 69.2544 32.9144 69.4393 32.3198 69.4393Z"
          fill="#2D5887"
        />
        <path
          d="M68.4988 58.341H55.4628C54.9343 58.341 54.5819 58.1561 54.5819 57.8786C54.5819 57.6012 54.9343 57.4162 55.4628 57.4162H68.4988C69.0273 57.4162 69.3796 57.6012 69.3796 57.8786C69.3796 58.1561 69.0273 58.341 68.4988 58.341Z"
          fill="white"
        />
        <path
          d="M96.2744 69.4393H55.4328C54.9223 69.4393 54.5819 69.2544 54.5819 68.9769C54.5819 68.6994 54.9223 68.5145 55.4328 68.5145H96.2744C96.785 68.5145 97.1253 68.6994 97.1253 68.9769C97.1253 69.2544 96.785 69.4393 96.2744 69.4393Z"
          fill="url(#empty_box_paint0)"
        />
        <path
          d="M68.5499 62.0405H50.7879C50.2899 62.0405 49.9579 61.8555 49.9579 61.578C49.9579 61.3006 50.2899 61.1156 50.7879 61.1156H68.5499C69.0479 61.1156 69.3799 61.3006 69.3799 61.578C69.3799 61.8555 69.0479 62.0405 68.5499 62.0405Z"
          fill="#2D5887"
        />
        <path
          d="M82.4245 65.7399H50.7862C50.2892 65.7399 49.9579 65.5549 49.9579 65.2775C49.9579 65 50.2892 64.815 50.7862 64.815H82.4245C82.9214 64.815 83.2527 65 83.2527 65.2775C83.0871 65.5549 82.7558 65.7399 82.4245 65.7399Z"
          fill="#2D5887"
        />
        <path
          d="M96.2248 65.7399H84.1536C83.6131 65.7399 83.2528 65.5549 83.2528 65.2775C83.2528 65 83.6131 64.815 84.1536 64.815H96.2248C96.7653 64.815 97.1256 65 97.1256 65.2775C97.1256 65.5549 96.7653 65.7399 96.2248 65.7399Z"
          fill="white"
        />
        <path
          d="M103.65 65.7399H98.9254C98.4005 65.7399 98.0505 65.5549 98.0505 65.2775C98.0505 65 98.4005 64.815 98.9254 64.815H103.65C104.175 64.815 104.525 65 104.525 65.2775C104.525 65.5549 104.175 65.7399 103.65 65.7399Z"
          fill="url(#empty_box_paint1)"
        />
        <path
          d="M1.86533 116.607H157.241"
          stroke="#D6DEE8"
          strokeWidth="2"
          strokeMiterlimit="10"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M110.998 57.4162V116.607H27.0802C23.7698 116.607 21.2871 113.947 21.2871 110.788V57.4162H110.998Z"
          fill="white"
          stroke="#D6DCE8"
          strokeWidth="2"
          strokeMiterlimit="10"
        />
        <path
          d="M144.293 57.4162V110.788C144.293 114.113 141.533 116.607 138.255 116.607H110.998V57.4162H144.293Z"
          fill="#DAE1ED"
        />
        <path
          d="M144.293 57.4162V110.788C144.293 114.113 141.533 116.607 138.255 116.607H110.998V57.4162H144.293Z"
          fill="#F1F3F9"
          stroke="#D6DCE8"
          strokeWidth="2"
          strokeMiterlimit="10"
        />
        <path
          d="M53.6574 57.4161L71.3036 29.6705H161.865L143.72 57.4161H53.6574Z"
          fill="#C5CDDB"
        />
        <path
          d="M53.6574 57.4161L71.3036 29.6705H161.865L143.72 57.4161H53.6574Z"
          fill="#F1F3F9"
          stroke="#D6DCE8"
          strokeWidth="2"
          strokeMiterlimit="10"
        />
        <path
          opacity="0.3"
          d="M144.293 57.5783V88.8613H120.015C117.761 88.8613 116.2 87.4025 115.853 85.2953L110.998 57.4162L144.293 57.5783Z"
          fill="url(#empty_box_paint2)"
        />
        <path
          d="M143.595 57.4162H110.998L127.55 82.5036C128.732 84.165 130.59 85.1618 132.448 85.1618H157.783C159.472 85.1618 160.654 83.1681 159.64 81.839L143.595 57.4162Z"
          fill="white"
          stroke="#D6DCE8"
          strokeWidth="2"
          strokeMiterlimit="10"
        />
        <path
          d="M110.998 57.4161L93.2286 29.6705H1.86533L20.3056 57.4161H110.998Z"
          fill="white"
          stroke="#D6DCE8"
          strokeWidth="2"
          strokeMiterlimit="10"
        />
        <path
          d="M66.0586 91.6358H31.0815C30.2643 91.6358 29.6105 91.0192 29.6105 90.2485C29.6105 89.4778 30.2643 88.8612 31.0815 88.8612H66.0586C66.8758 88.8612 67.5296 89.4778 67.5296 90.2485C67.3661 91.0192 66.8758 91.6358 66.0586 91.6358Z"
          fill="#DAE1ED"
        />
        <path
          d="M66.0586 98.1098H31.0815C30.2643 98.1098 29.6105 97.6987 29.6105 97.1849C29.6105 96.6711 30.2643 96.2601 31.0815 96.2601H66.0586C66.8758 96.2601 67.5296 96.6711 67.5296 97.1849C67.3661 97.6987 66.8758 98.1098 66.0586 98.1098Z"
          fill="#DAE1ED"
        />
        <path
          d="M47.5385 105.509H31.1045C30.2745 105.509 29.6105 105.098 29.6105 104.584C29.6105 104.07 30.2745 103.659 31.1045 103.659H47.5385C48.3685 103.659 49.0325 104.07 49.0325 104.584C48.8665 105.098 48.2025 105.509 47.5385 105.509Z"
          fill="#DAE1ED"
        />
      </g>
      <defs>
        <linearGradient
          id="empty_box_paint0"
          x1="54.7731"
          y1="68.9957"
          x2="97.1746"
          y2="68.9957"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#FF66A9" />
          <stop offset="1" stopColor="#F53689" />
        </linearGradient>
        <linearGradient
          id="empty_box_paint1"
          x1="98.0937"
          y1="65.2492"
          x2="104.523"
          y2="65.2492"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#83A6FF" />
          <stop offset="1" stopColor="#5A78FF" />
        </linearGradient>
        <linearGradient
          id="empty_box_paint2"
          x1="127.653"
          y1="89.5067"
          x2="127.653"
          y2="60.864"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0.00289017" stopColor="#606673" stopOpacity="0" />
          <stop offset="1" stopColor="#AAB2C5" />
        </linearGradient>
      </defs>
    </svg>
  );
}
